import { useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { formatDate } from '@repo/util/formatDate';

import GET_MMSES_FOR_ACCOUNT_Q from '../gql/getMmsRequestsForAccount.gql';
import GET_SMSES_FOR_ACCOUNT_Q from '../gql/getSmsRequestsForAccount.gql';
import GET_MESSAGE_REQUESTS_FOR_ACCOUNT_Q, {
  type MessageRequestNode,
} from '../gql/getMessageRequestsForAccount.gql';

import { Table, type TableColumn } from '@repo/ui/Table';
import {
  COMPACT_MESSAGE_COLUMN_WIDTHS,
  COMPACT_MESSAGES_MEDIA_QUERY,
  MOBILE_MESSAGES_MEDIA_QUERY,
  MessageDirectionCell,
  MessageLastStatusCell,
  MessageStatusCell,
  messageParties,
} from '@repo/ui/MessageCells';
import { useMediaQuery } from '@repo/hooks/useMediaQuery';
import { Pagination, decodePaging, type PagingSchema } from '@repo/ui/Pagination';

import { POLICIES } from '@/lib/Authorization';
import { useUser } from '@repo/hooks/useUser';
import { ButtonLink } from '@repo/ui/Button';
import { Link } from 'react-router';

import { Mercury } from '@repo/types/Models';
import type { UseZodFormReturn } from '@repo/hooks/useZodForm';
import type { UseZodQueryReturn } from '@repo/hooks/useZodQuery';
import MessageBodyCell from './MessageBodyCell';
import MessageBodyCellLazy from './MessageBodyCellLazy';

const PAGE_SIZE = 10;

const TimestampCell = ({ value }: { value: string | Date | null | undefined }) => {
  const formatted = formatDate(value as string | Date);
  if (!formatted) return <span>-</span>;
  const idx = formatted.indexOf(' ');
  const date = idx === -1 ? formatted : formatted.slice(0, idx);
  const time = idx === -1 ? '' : formatted.slice(idx + 1);
  return (
    <div className="flex flex-col leading-tight">
      <span className="whitespace-nowrap">{date}</span>
      {time && <span className="whitespace-nowrap ">{time}</span>}
    </div>
  );
};

export interface MmsesTableProps<T extends PagingSchema> {
  accountId?: string;
  queryList?: Mercury.Queries.QueryList;
  page?: string | undefined;
  filter: UseZodFormReturn<T> | UseZodQueryReturn<T>;
  service: string | null;
  /** Reports the loaded row count (null while loading) so parents can gate exports. */
  onLoadedCountChange?: (count: number | null) => void;
}

export const MessagesTable = <T extends PagingSchema = PagingSchema>({
  accountId,
  queryList,
  page,
  filter,
  service,
  onLoadedCountChange,
}: MmsesTableProps<T>) => {
  const user = useUser();

  const canViewSms = POLICIES['sms_requests:show'](user);
  const canViewMms = POLICIES['mms_requests:show'](user);

  const isAll = service === 'all';
  // Phone cards and wide screens use the compact columns; in-between screens scroll sideways
  const isPhone = useMediaQuery(MOBILE_MESSAGES_MEDIA_QUERY);
  const compact = useMediaQuery(COMPACT_MESSAGES_MEDIA_QUERY) || isPhone;

  const {
    loading: smsLoading,
    error: smsError,
    data: smsData,
  } = useQuery(GET_SMSES_FOR_ACCOUNT_Q, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      id: accountId,
    },
    skip: service !== 'smses',
    notifyOnNetworkStatusChange: true,
  });

  const {
    loading: mmsLoading,
    error: mmsError,
    data: mmsData,
  } = useQuery(GET_MMSES_FOR_ACCOUNT_Q, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      id: accountId,
    },
    skip: service !== 'mmses',
    notifyOnNetworkStatusChange: true,
  });

  const {
    loading: allLoading,
    error: allError,
    data: allData,
  } = useQuery(GET_MESSAGE_REQUESTS_FOR_ACCOUNT_Q, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      id: accountId,
    },
    skip: !isAll,
    notifyOnNetworkStatusChange: true,
  });

  const loading = isAll ? allLoading : service === 'smses' ? smsLoading : mmsLoading;
  const error = isAll ? allError : service === 'smses' ? smsError : mmsError;

  const pageInfo = isAll
    ? allData?.messageRequestsForAccount.pageInfo
    : service === 'smses'
      ? smsData?.smsRequestsForAccount.pageInfo
      : mmsData?.mmsRequestsForAccount.pageInfo;

  const nodes = isAll
    ? allData?.messageRequestsForAccount?.edges?.map((message) => message.node)
    : service === 'smses'
      ? smsData?.smsRequestsForAccount?.edges?.map((message) => message.node)
      : mmsData?.mmsRequestsForAccount?.edges?.map((message) => message.node);

  const loadedCount = nodes?.length ?? 0;
  useEffect(() => {
    onLoadedCountChange?.(loading ? null : loadedCount);
  }, [loading, loadedCount, onLoadedCountChange]);

  type Row = MessageRequestNode;

  // "SMS"/"MMS" from the row's type on the combined list, else from the selected service
  const serviceLabel = (entry: Row) => {
    const type = (entry as { type?: string }).type;
    if (type === 'SMS_REQUEST' || (!type && service === 'smses')) return 'SMS';
    if (type === 'MMS_REQUEST' || (!type && service === 'mmses')) return 'MMS';
    return '';
  };

  // Shared by the full and compact column sets
  const actionsColumn: TableColumn<Row> = {
    title: 'Actions',
    width: 'md',
    pin: 'right',
    isAction: true,
    Cell({ entry }) {
      const e = entry as { id: string; type?: string };
      const entryType = e.type;
      const isSmsRow = service === 'smses' || (isAll && entryType === 'SMS_REQUEST');
      const isMmsRow = service === 'mmses' || (isAll && entryType === 'MMS_REQUEST');

      const showBody = canViewSms && isSmsRow;
      const detailsSegment = isSmsRow ? 'smses' : isMmsRow ? 'mmses' : null;
      const showDetails =
        detailsSegment !== null && ((isSmsRow && canViewSms) || (isMmsRow && canViewMms));

      if (!showBody && !showDetails) return <></>;

      const currentSms =
        service === 'smses'
          ? smsData?.smsRequestsForAccount.edges.find((edge) => edge.node.id === e.id)?.node
          : undefined;

      return (
        <div className="flex flex-row flex-wrap gap-1.5 md:flex-col md:items-start">
          {showBody &&
            (isAll ? (
              <MessageBodyCellLazy id={e.id} />
            ) : (
              currentSms && <MessageBodyCell sms={currentSms} />
            ))}
          {showDetails && (
            <ButtonLink
              as={Link}
              size="sm"
              variant="secondary"
              icon="mystery"
              href={`/a/${accountId}/messaging/${detailsSegment}/${e.id}`}
            >
              Details
            </ButtonLink>
          )}
        </div>
      );
    },
  };

  // Narrower screens: every field in its own column; the table scrolls sideways
  const fullColumns: TableColumn<Row>[] = [
    {
      title: 'Status',
      width: 'md',
      pin: 'left',
      Cell({ entry: { status, errorCode } }) {
        return (
          <div className="flex relative gap-2 items-center">
            <div
              className={`h-2.5 w-2.5 rounded-full ${
                errorCode === 'OK' ? 'bg-green-500' : 'bg-red-500'
              } ${status === 'UNDELIVERED' ? 'bg-yellow-500' : ''}`}
            ></div>
            <span>{status}</span>
          </div>
        );
      },
    },

    {
      title: 'Service',
      width: 80,
      pin: 'left',
      Cell({ entry }) {
        return <span>{serviceLabel(entry)}</span>;
      },
    },

    {
      title: 'Host number',
      field: 'hostNumber',
      width: 'md',
    },
    {
      title: 'Remote number',
      field: 'remoteNumber',
      width: 'md',
    },

    {
      title: 'Direction',
      field: 'direction',
      width: 'md',
    },

    {
      title: 'Created at',
      width: 'md',
      Cell({ entry: { insertedAt } }) {
        return <TimestampCell value={insertedAt} />;
      },
    },

    {
      title: 'Sent at',
      width: 'md',
      Cell({ entry: { processedAt } }) {
        return <TimestampCell value={processedAt} />;
      },
    },

    {
      title: 'Delivered at',
      width: 'md',
      Cell({ entry: { deliveredAt } }) {
        return <TimestampCell value={deliveredAt} />;
      },
    },

    {
      title: 'Error',
      width: 'xl',
      Cell({ entry: { errorCode } }) {
        if (!errorCode || errorCode === 'OK') return <span className="text-grey-500">-</span>;
        return (
          <div className="font-mono text-xs break-all text-red-700 dark:text-red-300">
            {errorCode}
          </div>
        );
      },
    },
    actionsColumn,
  ];

  // Laptop and desktop: tightly sized columns, with status + error and all
  // timestamps merged, so the table fits without sideways scrolling
  const compactColumns: TableColumn<Row>[] = [
    {
      title: 'Status',
      width: COMPACT_MESSAGE_COLUMN_WIDTHS.status,
      Cell({ entry: { status, errorCode } }) {
        return <MessageStatusCell status={status} errorCode={errorCode} />;
      },
    },
    {
      title: 'Service',
      width: COMPACT_MESSAGE_COLUMN_WIDTHS.service,
      Cell({ entry }) {
        return <span>{serviceLabel(entry)}</span>;
      },
    },
    {
      title: 'Direction',
      width: COMPACT_MESSAGE_COLUMN_WIDTHS.direction,
      Cell({ entry: { direction } }) {
        return <MessageDirectionCell direction={direction} />;
      },
    },
    {
      title: 'From',
      width: COMPACT_MESSAGE_COLUMN_WIDTHS.number,
      Cell({ entry: { hostNumber, remoteNumber, direction } }) {
        return <span>{messageParties(hostNumber, remoteNumber, direction).from ?? '-'}</span>;
      },
    },
    {
      title: 'To',
      width: COMPACT_MESSAGE_COLUMN_WIDTHS.number,
      Cell({ entry: { hostNumber, remoteNumber, direction } }) {
        return <span>{messageParties(hostNumber, remoteNumber, direction).to ?? '-'}</span>;
      },
    },
    {
      title: 'Last status',
      width: COMPACT_MESSAGE_COLUMN_WIDTHS.lastStatus,
      Cell({ entry: { insertedAt, processedAt, deliveredAt } }) {
        return (
          <MessageLastStatusCell
            insertedAt={insertedAt}
            processedAt={processedAt}
            deliveredAt={deliveredAt}
          />
        );
      },
    },
    { ...actionsColumn, width: COMPACT_MESSAGE_COLUMN_WIDTHS.actions, pin: undefined },
  ];

  return (
    <div>
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={(nodes ?? []) as MessageRequestNode[]}
        dataType="messages"
        error={!!error}
        textSm={true}
        columns={compact ? compactColumns : fullColumns}
      ></Table>
      {!error && <Pagination {...{ filter, pageInfo }} />}
    </div>
  );
};
